import React, { useEffect, useState, useMemo } from 'react';
import { Select, Button, Space, message, Collapse, Input, Checkbox } from 'antd';
import axios from '../utils/http';

// 轻量版六险核对组件，仅用于保险管理总页面下的一个 Tab，避免出现内部再嵌套列表/批次等重复标签。
export default function SixInsuranceReconcile(){
  // 各险种批次
  const [commercialBatches, setCommercialBatches] = useState([]); // 雇主/安责视为商业
  const [medicalBatches, setMedicalBatches] = useState([]);
  const [workInjuryBatches, setWorkInjuryBatches] = useState([]);
  const [unemploymentBatches, setUnemploymentBatches] = useState([]);
  const [maternityBatches, setMaternityBatches] = useState([]);
  const [pensionBatches, setPensionBatches] = useState([]);
  const [loadingMap, setLoadingMap] = useState({});

  // 已选择批次
  const [commercialBatch, setCommercialBatch] = useState();
  const [medicalBatch, setMedicalBatch] = useState();
  const [workInjuryBatch, setWorkInjuryBatch] = useState();
  const [unemploymentBatch, setUnemploymentBatch] = useState();
  const [maternityBatch, setMaternityBatch] = useState();
  const [pensionBatch, setPensionBatch] = useState();

  // 人员类别 active | temp | intern
  const [employeeCategories, setEmployeeCategories] = useState(['active']);
  const [sixLoading, setSixLoading] = useState(false);
  const [sixResult, setSixResult] = useState(null);
  const [sixError, setSixError] = useState(null);
  // 缺失筛选
  const [missingNameKeyword, setMissingNameKeyword] = useState('');
  const [missingTypesFilter, setMissingTypesFilter] = useState([]); // 选定缺失的险种过滤
  const [onlyMultiMissing, setOnlyMultiMissing] = useState(false); // 只看缺少>=2险种

  const loadBatchList = async (kind) => {
    setLoadingMap(m => ({...m, [kind]: true}));
    try {
      let url;
      switch(kind){
  case 'commercial': url = '/contributions/commercial/import-batches'; break;
        case 'medical': url = '/medical-insurance-batches/import-batches'; break;
        case 'work_injury': url = '/contributions/work_injury/import-batches'; break;
        case 'unemployment': url = '/contributions/unemployment/import-batches'; break;
        case 'maternity': url = '/contributions/maternity/import-batches'; break;
        case 'pension': url = '/contributions/pension/import-batches'; break;
        default: return;
      }
      const res = await axios.get(url).catch(err => {
        if (err?.response?.status === 404) {
          // 后端不支持该险种：保持空数组即可，不弹错误
          return { data:{ data:[] } };
        }
        throw err;
      });
      const opts = (res.data.data||[]).map(b=>({ value:b.import_batch, label:`${b.import_batch} (${b.count})` }));
      if (kind==='commercial') setCommercialBatches(opts);
      if (kind==='medical') setMedicalBatches(opts);
      if (kind==='work_injury') setWorkInjuryBatches(opts);
      if (kind==='unemployment') setUnemploymentBatches(opts);
      if (kind==='maternity') setMaternityBatches(opts);
      if (kind==='pension') setPensionBatches(opts);
  } catch(e){ if (e?.response?.status !== 404) message.error(e?.message || '加载批次失败'); }
    setLoadingMap(m => ({...m, [kind]: false}));
  };

  // 初次默认加载商业批次 + 恢复缓存
  useEffect(()=>{
    try {
      const cache = localStorage.getItem('six_insurance_reconcile_state');
      if (cache) {
        const obj = JSON.parse(cache);
        setCommercialBatch(obj.commercialBatch);
        setMedicalBatch(obj.medicalBatch);
        setWorkInjuryBatch(obj.workInjuryBatch);
        setUnemploymentBatch(obj.unemploymentBatch);
        setMaternityBatch(obj.maternityBatch);
        setPensionBatch(obj.pensionBatch);
        setEmployeeCategories(obj.employeeCategories || ['active']);
        setSixResult(obj.sixResult || null);
      }
    } catch(e) { /* ignore */ }
    loadBatchList('commercial');
  }, []);

  // 缓存当前选择及结果（结果更新或选择变更时）
  useEffect(()=>{
    const payload = {
      commercialBatch, medicalBatch, workInjuryBatch, unemploymentBatch, maternityBatch, pensionBatch,
      employeeCategories, sixResult
    };
    try { localStorage.setItem('six_insurance_reconcile_state', JSON.stringify(payload)); } catch(e){/* ignore */}
  }, [commercialBatch, medicalBatch, workInjuryBatch, unemploymentBatch, maternityBatch, pensionBatch, employeeCategories, sixResult]);

  const fetchSixReconcile = async () => {
    // 如果一个批次都没选，则提示
    if (!commercialBatch && !medicalBatch && !workInjuryBatch && !unemploymentBatch && !maternityBatch && !pensionBatch) {
      message.warning('请至少选择一个险种批次');
      return;
    }
    setSixLoading(true); setSixError(null);
    try {
      const params = {
        employeeCategories: employeeCategories.join(','),
        commercial_batch: commercialBatch,
        employer_batch: commercialBatch, // 兼容后端旧字段
        medical_batch: medicalBatch,
        work_injury_batch: workInjuryBatch,
        unemployment_batch: unemploymentBatch,
        maternity_batch: maternityBatch,
        pension_batch: pensionBatch
      };
      const res = await axios.get('/insurance/reconcile', { params });
      setSixResult(res.data);
    } catch(e){ setSixError(e?.message || '核对失败'); }
    setSixLoading(false);
  };

  const exportMissing = () => {
    if (!sixResult || !(sixResult.missingEmployees||[]).length) { message.info('无缺失数据可导出'); return; }
    const rows = ["ID,姓名,部门,岗位", ...(sixResult.missingEmployees||[]).map(e=>`${e.id},${e.name},${e.department||''},${e.position||''}`)];
    const blob = new Blob([rows.join('\n')], { type:'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob); const a=document.createElement('a'); a.href=url; a.download=`missing_employees_${sixResult.batch}.csv`; a.click(); URL.revokeObjectURL(url);
  };

  return (
    <div style={{ padding:4 }}>
      <Space direction="vertical" style={{ width:'100%' }} size="large">
        <div style={{ display:'flex', flexWrap:'wrap', gap:12, alignItems:'flex-end' }}>
          <div style={{ minWidth:240 }}>
            <label style={{ fontSize:12, color:'#475569' }}>人员类别(可多选)</label>
            <Select mode="multiple" maxTagCount="responsive" style={{ width:'100%', marginTop:4 }} value={employeeCategories} onChange={v=>setEmployeeCategories(v)} options={[
              { value:'active', label:'在职' },
              { value:'temp', label:'临时' },
              { value:'intern', label:'实习' }
            ]} />
          </div>
          <Button type="primary" onClick={fetchSixReconcile} loading={sixLoading}>执行核对</Button>
          <Button onClick={()=>{ setCommercialBatch(undefined); setMedicalBatch(undefined); setWorkInjuryBatch(undefined); setUnemploymentBatch(undefined); setMaternityBatch(undefined); setPensionBatch(undefined); setSixResult(null); }} disabled={sixLoading}>清空选择</Button>
          <Button onClick={()=>{ localStorage.removeItem('six_insurance_reconcile_state'); message.success('已清除缓存'); }}>清除缓存</Button>
        </div>
        <Collapse style={{ width:'100%' }} size="small" items={[
          {
            key:'batches',
            label: `选择险种批次 (已选 ${[commercialBatch,medicalBatch,workInjuryBatch,unemploymentBatch,maternityBatch,pensionBatch].filter(Boolean).length} 项)` ,
            children: (
              <div style={{ display:'flex', flexWrap:'wrap', gap:16 }}>
                <div style={{ minWidth:220 }}>
                  <label style={{ fontSize:12 }}>商业(雇主/安责)</label>
                  <Select allowClear placeholder={commercialBatches.length?"商业批次":"未支持/无数据"} style={{ width:'100%', marginTop:4 }} value={commercialBatch} options={commercialBatches} loading={loadingMap.commercial} onDropdownVisibleChange={(o)=> o && commercialBatches.length===0 && loadBatchList('commercial')} onChange={setCommercialBatch} />
                </div>
                <div style={{ minWidth:220 }}>
                  <label style={{ fontSize:12 }}>医疗</label>
                  <Select allowClear placeholder={medicalBatches.length?"医疗批次":"未支持/无数据"} style={{ width:'100%', marginTop:4 }} value={medicalBatch} options={medicalBatches} loading={loadingMap.medical} onDropdownVisibleChange={(o)=> o && medicalBatches.length===0 && loadBatchList('medical')} onChange={setMedicalBatch} />
                </div>
                <div style={{ minWidth:220 }}>
                  <label style={{ fontSize:12 }}>工伤</label>
                  <Select allowClear placeholder={workInjuryBatches.length?"工伤批次":"未支持/无数据"} style={{ width:'100%', marginTop:4 }} value={workInjuryBatch} options={workInjuryBatches} loading={loadingMap.work_injury} onDropdownVisibleChange={(o)=> o && workInjuryBatches.length===0 && loadBatchList('work_injury')} onChange={setWorkInjuryBatch} />
                </div>
                <div style={{ minWidth:220 }}>
                  <label style={{ fontSize:12 }}>失业</label>
                  <Select allowClear placeholder={unemploymentBatches.length?"失业批次":"未支持/无数据"} style={{ width:'100%', marginTop:4 }} value={unemploymentBatch} options={unemploymentBatches} loading={loadingMap.unemployment} onDropdownVisibleChange={(o)=> o && unemploymentBatches.length===0 && loadBatchList('unemployment')} onChange={setUnemploymentBatch} />
                </div>
                <div style={{ minWidth:220 }}>
                  <label style={{ fontSize:12 }}>生育</label>
                  <Select allowClear placeholder={maternityBatches.length?"生育批次":"未支持/无数据"} style={{ width:'100%', marginTop:4 }} value={maternityBatch} options={maternityBatches} loading={loadingMap.maternity} onDropdownVisibleChange={(o)=> o && maternityBatches.length===0 && loadBatchList('maternity')} onChange={setMaternityBatch} />
                </div>
                <div style={{ minWidth:220 }}>
                  <label style={{ fontSize:12 }}>养老</label>
                  <Select allowClear placeholder={pensionBatches.length?"养老批次":"未支持/无数据"} style={{ width:'100%', marginTop:4 }} value={pensionBatch} options={pensionBatches} loading={loadingMap.pension} onDropdownVisibleChange={(o)=> o && pensionBatches.length===0 && loadBatchList('pension')} onChange={setPensionBatch} />
                </div>
                <div style={{ display:'flex', flexDirection:'column', gap:4 }}>
                  <Button size="small" type="dashed" onClick={()=>{
                    // 选中每个险种的第一个批次（最新已按接口排序 last_time DESC）
                    if (commercialBatches.length) setCommercialBatch(commercialBatches[0].value);
                    if (medicalBatches.length) setMedicalBatch(medicalBatches[0].value);
                    if (workInjuryBatches.length) setWorkInjuryBatch(workInjuryBatches[0].value);
                    if (unemploymentBatches.length) setUnemploymentBatch(unemploymentBatches[0].value);
                    if (maternityBatches.length) setMaternityBatch(maternityBatches[0].value);
                    if (pensionBatches.length) setPensionBatch(pensionBatches[0].value);
                  }}>一键选中最新批次</Button>
                  <Button size="small" onClick={()=>{
                    setCommercialBatch(undefined);
                    setMedicalBatch(undefined);
                    setWorkInjuryBatch(undefined);
                    setUnemploymentBatch(undefined);
                    setMaternityBatch(undefined);
                    setPensionBatch(undefined);
                  }}>清空批次</Button>
                </div>
              </div>
            )
          }
        ]} />
        {sixError && <div style={{ color:'#dc2626', fontSize:12 }}>{sixError}</div>}
        {!sixError && sixResult && (
          <div style={{ background:'#f8fafc', border:'1px solid #e2e8f0', borderRadius:8, padding:'12px 14px', width:'100%' }}>
            <h4 style={{ margin:'0 0 8px', fontSize:14 }}>核对结果</h4>
            <div style={{ fontSize:12, lineHeight:1.6 }}>
              批次：<b>{sixResult.batch}</b>；范围：<b>{sixResult.scope}</b><br />
              在职人数：{sixResult.totalEmployees}；覆盖人数：{sixResult.coveredEmployeesCount}；覆盖率：<b style={{ color: sixResult.coveragePercent>=80?'#16a34a':'#ea580c' }}>{sixResult.coveragePercent}%</b>
            </div>
            <div style={{ marginTop:10 }}>
              <div style={{ height:10, background:'#e2e8f0', borderRadius:5, overflow:'hidden', position:'relative' }}>
                <div style={{ position:'absolute', left:0, top:0, bottom:0, width:`${sixResult.coveragePercent}%`, background: sixResult.coveragePercent>=80?'#16a34a':'#f59e0b', transition:'width .4s' }} />
              </div>
            </div>
            <div style={{ marginTop:12, display:'flex', flexWrap:'wrap', gap:10 }}>
              {(sixResult.selectedTypes || Object.keys(sixResult.coverageSummary||{})).map(k => {
                const v = (sixResult.coverageSummary||{})[k] || { count:0, percent:0, totalEmployees: sixResult.totalEmployees };
                return (
                  <div key={k} style={{ flex:'1 1 160px', minWidth:160, background:'#fff', border:'1px solid #e2e8f0', borderRadius:8, padding:'8px 10px' }}>
                    <div style={{ fontSize:12, fontWeight:600 }}>{k}</div>
                    <div style={{ fontSize:11, color:'#475569' }}>覆盖人数：{v.count} / {v.totalEmployees} ({v.percent}%)</div>
                  </div>
                );
              })}
            </div>
            {/* 简易覆盖图表：SVG 条形 */}
            <div style={{ marginTop:18 }}>
              <h5 style={{ margin:'0 0 8px', fontSize:13 }}>险种覆盖可视化</h5>
              <svg width="100%" height={ ( (sixResult.selectedTypes||[]).length || 1) * 22 } style={{ background:'#fff', border:'1px solid #e2e8f0', borderRadius:6 }}>
                { (sixResult.selectedTypes||[]).map((k, idx) => {
                  const v = (sixResult.coverageSummary||{})[k] || { percent:0, count:0, totalEmployees:sixResult.totalEmployees };
                  const barWidth = Math.max(0, Math.min(100, v.percent));
                  return (
                    <g key={k} transform={`translate(6, ${idx*22+4})`}>
                      <text x={0} y={10} fontSize={11} fill="#334155">{k}</text>
                      <rect x={70} y={2} width={barWidth*4} height={12} rx={3} fill={barWidth>=80? '#16a34a':'#0ea5e9'} />
                      <rect x={70+barWidth*4} y={2} width={(100-barWidth)*4} height={12} rx={3} fill="#e2e8f0" />
                      <text x={70+ (100*4) + 8} y={12} fontSize={11} fill="#475569">{v.count}/{v.totalEmployees} {v.percent}%</text>
                    </g>
                  );
                })}
              </svg>
            </div>
            <div style={{ marginTop:16 }}>
              <h5 style={{ margin:'0 0 6px', fontSize:13 }}>缺失员工（{(sixResult.missingEmployees||[]).length}）</h5>
              <div style={{ display:'flex', flexWrap:'wrap', gap:12, marginBottom:8, alignItems:'center' }}>
                <Input allowClear placeholder="姓名关键字" value={missingNameKeyword} onChange={e=>setMissingNameKeyword(e.target.value)} style={{ width:160 }} size="small" />
                <Select
                  mode="multiple"
                  allowClear
                  placeholder="按缺失险种过滤"
                  size="small"
                  style={{ minWidth:220 }}
                  value={missingTypesFilter}
                  onChange={setMissingTypesFilter}
                  options={(sixResult.selectedTypes||[]).map(t=>({ value:t, label:t }))}
                />
                <Checkbox checked={onlyMultiMissing} onChange={e=>setOnlyMultiMissing(e.target.checked)}>仅显示缺少2个及以上险种</Checkbox>
              </div>
              {(sixResult.missingEmployees||[]).length === 0 && <div style={{ fontSize:12, color:'#16a34a' }}>全部覆盖，无缺失。</div>}
              {(sixResult.missingEmployees||[]).length > 0 && (
                <div style={{ maxHeight:260, overflow:'auto', border:'1px solid #e2e8f0', borderRadius:6 }}>
                  <table style={{ width:'100%', borderCollapse:'collapse', fontSize:12 }}>
                    <thead><tr style={{ background:'#f1f5f9' }}><th style={{ padding:'6px 8px', textAlign:'left', width:50 }}>ID</th><th style={{ padding:'6px 8px', textAlign:'left', width:120 }}>姓名</th><th style={{ padding:'6px 8px', textAlign:'left', width:160 }}>部门</th><th style={{ padding:'6px 8px', textAlign:'left', width:160 }}>岗位</th><th style={{ padding:'6px 8px', textAlign:'left' }}>缺失险种</th></tr></thead>
                    <tbody>
                      {(sixResult.missingEmployees||[])
                        .filter(e => !missingNameKeyword || (e.name||'').includes(missingNameKeyword))
                        .filter(e => !onlyMultiMissing || ((e.missingTypes||[]).length >= 2))
                        .filter(e => !missingTypesFilter.length || missingTypesFilter.every(t => (e.missingTypes||[]).includes(t)))
                        .map(e => (
                        <tr key={e.id} style={{ borderBottom:'1px solid #e2e8f0' }}>
                          <td style={{ padding:'6px 8px' }}>{e.id}</td>
                          <td style={{ padding:'6px 8px' }}>{e.name}</td>
                          <td style={{ padding:'6px 8px' }}>{e.department || '-'}</td>
                          <td style={{ padding:'6px 8px' }}>{e.position || '-'}</td>
                          <td style={{ padding:'6px 8px' }}>{(e.missingTypes||[]).join('、')}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  <div style={{ marginTop:8 }}>
                    <Button size="small" onClick={()=>{
                      if(!sixResult || !(sixResult.missingEmployees||[]).length){ message.info('无缺失数据'); return; }
                      const filtered = (sixResult.missingEmployees||[])
                        .filter(e => !missingNameKeyword || (e.name||'').includes(missingNameKeyword))
                        .filter(e => !onlyMultiMissing || ((e.missingTypes||[]).length >= 2))
                        .filter(e => !missingTypesFilter.length || missingTypesFilter.every(t => (e.missingTypes||[]).includes(t)));
                      const rows = ["ID,姓名,部门,岗位,缺失险种", ...filtered.map(e=>`${e.id},${e.name},${e.department||''},${e.position||''},${(e.missingTypes||[]).join('|')}`)];
                      const blob = new Blob([rows.join('\n')], { type:'text/csv;charset=utf-8;' });
                      const url = URL.createObjectURL(blob); const a=document.createElement('a'); a.href=url; a.download=`missing_employees_${sixResult.batch}.csv`; a.click(); URL.revokeObjectURL(url);
                    }}>导出缺失名单 CSV</Button>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
  {!sixError && !sixResult && (commercialBatch||medicalBatch||workInjuryBatch||unemploymentBatch||maternityBatch||pensionBatch) && !sixLoading && <div style={{ fontSize:12, color:'#64748b' }}>暂无数据，请执行核对。</div>}
  {!commercialBatch && !medicalBatch && !workInjuryBatch && !unemploymentBatch && !maternityBatch && !pensionBatch && <div style={{ fontSize:12, color:'#64748b' }}>请选择至少一个险种批次。</div>}
      </Space>
    </div>
  );
}
